import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ExternalLink, Phone } from 'lucide-react'
import DealDetail from '@/components/DealDetail'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/layout/Card'
import Select from '@/components/ui/Select'
import FocusTasks from '@/features/tasks/FocusTasks'
import { GROUP_ORDER, groupOf, type TaskGroup } from '@/features/tasks/taskGroups'
import { useFocus } from '@/features/attention/useAttention'
import { useTasks } from '@/features/tasks/useTasks'
import { useCurrency } from '@/features/agency/useCurrency'
import { formatCompactValue, formatShortDate, relativeDay } from '@/lib/dealFormat'
import { HEALTH_BADGE, HEALTH_META } from '@/lib/dealHealth'
import { cn } from '@/lib/cn'
import type { FocusDeal, FocusPriority } from '@/api/attention'

type Tab = 'deals' | 'tasks'

const PRIORITY_LABEL: Record<FocusPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
}
/** Severity, so status colours — always with the word beside them, never colour alone. */
const PRIORITY_PILL: Record<FocusPriority, string> = {
  critical: 'bg-red-50 text-red-700',
  high: 'bg-amber-50 text-amber-800',
  medium: 'bg-gray-100 text-gray-600',
}

function PriorityPill({ priority }: { priority: FocusPriority }) {
  return (
    <span
      className={cn(
        'shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5',
        PRIORITY_PILL[priority],
      )}
    >
      {PRIORITY_LABEL[priority]}
    </span>
  )
}

/** A summary figure that is also a way in: it switches to the list it counts. */
function SummaryCard({
  label,
  value,
  active,
  tone,
  onClick,
}: {
  label: string
  value: number
  active: boolean
  tone?: 'critical' | 'overdue'
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'text-left bg-white border rounded-xl px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40',
        active ? 'border-brand-primary' : 'border-gray-200 hover:border-brand-primary/40',
      )}
    >
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div
        className={cn(
          'text-2xl font-semibold mt-0.5',
          value > 0 && tone ? 'text-red-700' : 'text-brand-darkest',
        )}
      >
        {value}
      </div>
    </button>
  )
}

// --- priority deals ---------------------------------------------------------------------------

function DealRow({
  deal,
  selected,
  currency,
  onSelect,
  onOpen,
}: {
  deal: FocusDeal
  selected: boolean
  currency: string
  onSelect: () => void
  onOpen: () => void
}) {
  const [top, ...rest] = deal.factors
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected}
        className={cn(
          'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary/40',
          selected ? 'bg-brand-bg' : 'hover:bg-gray-50',
        )}
      >
        <PriorityPill priority={deal.priority} />
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium text-brand-dark truncate">{deal.title}</span>
            {deal.value != null && (
              <span className="ml-auto text-xs text-gray-500 tabular-nums shrink-0">
                {formatCompactValue(deal.value, currency)}
              </span>
            )}
          </span>
          <span className="block text-xs text-gray-500 truncate">
            {top?.reason}
            {rest.length > 0 && <span className="text-gray-400"> · +{rest.length} more</span>}
            {deal.ownerName && ` · ${deal.ownerName}`}
          </span>
        </span>
      </button>
      {selected && (
        // Narrow screens have no room for the side panel beside the list, so the "why" opens in place.
        <div className="lg:hidden bg-brand-bg px-4 pt-px pb-3 space-y-3">
          <FactorList deal={deal} />
          <DealActions deal={deal} onOpen={onOpen} />
        </div>
      )}
    </li>
  )
}

/** What adds up to the score — "+40 Next contact 74 days overdue" — biggest first. */
function FactorList({ deal }: { deal: FocusDeal }) {
  return (
    <ul className="mt-1.5 space-y-1">
      {deal.factors.map((f) => (
        <li key={f.reason} className="flex items-baseline gap-2 text-sm">
          <span className="w-9 shrink-0 text-right tabular-nums font-semibold text-brand-darkest">
            +{f.points}
          </span>
          <span className="text-brand-dark">{f.reason}</span>
        </li>
      ))}
    </ul>
  )
}

/** Act on the deal: open it, call its contact, or keep the list and open it in a new tab. */
function DealActions({ deal, onOpen }: { deal: FocusDeal; onOpen: () => void }) {
  const phone = deal.contact?.phone
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={onOpen}
        className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark"
      >
        Open deal
      </button>
      {phone && (
        <a
          href={`tel:${phone.replace(/[^\d+]/g, '')}`}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-brand-dark hover:border-brand-primary/40 flex items-center gap-1.5"
          title={`Call ${deal.contact?.name ?? ''}`}
        >
          <Phone size={14} /> Call {deal.contact?.name?.split(' ')[0]}
        </a>
      )}
      <a
        href={`/deals/${deal.dealId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-brand-primary/40 flex items-center gap-1.5"
        title="Open the deal in a new tab"
      >
        <ExternalLink size={14} /> New tab
      </a>
    </div>
  )
}

/**
 * Why the selected deal is here — the answer to "what's this number?". Each factor with what it adds,
 * then the facts and the actions to take on it.
 */
function InsightPanel({
  deal,
  currency,
  onOpen,
}: {
  deal: FocusDeal
  currency: string
  onOpen: () => void
}) {
  const health = HEALTH_META[deal.health]
  return (
    <Card
      title={
        <span className="flex items-center gap-2 min-w-0">
          <PriorityPill priority={deal.priority} />
          <span className="truncate">{deal.title}</span>
        </span>
      }
      className="hidden lg:block lg:sticky lg:top-6"
    >
      <div className="space-y-4">
        <div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium text-gray-500">Why it’s here</span>
            <span className="text-xs text-gray-400 tabular-nums">{deal.score} points</span>
          </div>
          <FactorList deal={deal} />
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm border-t border-gray-100 pt-3">
          <div>
            <dt className="text-xs text-gray-500">Health</dt>
            <dd className="mt-0.5">
              <span
                className={cn(
                  'text-[11px] font-semibold rounded-full px-2 py-0.5',
                  HEALTH_BADGE[health.tone],
                )}
              >
                {health.label}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Value</dt>
            <dd className="text-brand-dark">
              {deal.value == null ? '—' : formatCompactValue(deal.value, currency)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Next contact</dt>
            <dd className="text-brand-dark">
              {deal.nextContactDate ? formatShortDate(deal.nextContactDate) : 'Not set'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Last contact</dt>
            <dd className="text-brand-dark">
              {deal.lastContactedAt ? relativeDay(deal.lastContactedAt) : 'Never'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Stage</dt>
            <dd className="text-brand-dark truncate">{deal.stageName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Owner</dt>
            <dd className="text-brand-dark truncate">{deal.ownerName ?? 'Unassigned'}</dd>
          </div>
        </dl>

        <div className="border-t border-gray-100 pt-3">
          <DealActions deal={deal} onOpen={onOpen} />
        </div>
      </div>
    </Card>
  )
}

// --- page -------------------------------------------------------------------------------------

/**
 * Focus — where to work now. Priority deals rank every open deal by the factors that make it worth
 * attention (late follow-ups, long silences, value, a lead reading our emails); Tasks lists the concrete
 * actions due. The dashboard observes; this is where the work happens.
 */
export default function Focus() {
  const [params, setParams] = useSearchParams()
  const tab: Tab = params.get('tab') === 'tasks' ? 'tasks' : 'deals'
  // A task tile (Overdue / Due today / Next 7 days) narrows the task list to its own group.
  const dueParam = params.get('due')
  const taskGroup = tab === 'tasks' && GROUP_ORDER.includes(dueParam as TaskGroup) ? (dueParam as TaskGroup) : null
  const setTab = (t: Tab) => setParams(t === 'tasks' ? { tab: 'tasks' } : {}, { replace: true })
  const showTasks = (g: TaskGroup | null) => setParams(g ? { tab: 'tasks', due: g } : { tab: 'tasks' }, { replace: true })

  const [ownerId, setOwnerId] = useState<string | null>(null)
  const [tier, setTier] = useState<FocusPriority | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [openDeal, setOpenDeal] = useState<string | null>(null)
  const currency = useCurrency()

  const { data, isLoading, isPlaceholderData } = useFocus(ownerId)
  const { data: openTasks } = useTasks('open')

  const deals = (data?.deals ?? []).filter((d) => !tier || d.priority === tier)
  const selected = deals.find((d) => d.dealId === selectedId) ?? deals[0] ?? null
  const owners = data?.owners ?? []

  const myTasks = (openTasks ?? []).filter((t) => !ownerId || t.assigneeUserId === ownerId)
  const taskCount = (g: TaskGroup) => myTasks.filter((t) => groupOf(t) === g).length

  const showDeals = (t: FocusPriority | null) => {
    setTier(t)
    setTab('deals')
  }

  return (
    <PageContainer width="full">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-brand-darkest">Focus</h1>
          <p className="text-sm text-gray-500">
            What to work on now — the deals that most need you, and the tasks due.
          </p>
        </div>
        {owners.length > 1 && (
          <Select
            aria-label="Person"
            value={ownerId ?? ''}
            onChange={(e) => {
              setOwnerId(e.target.value || null)
              setSelectedId(null)
            }}
            className="min-w-[10rem]"
          >
            <option value="">Everyone</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        )}
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-5">
        <SummaryCard
          label="Critical"
          value={data?.summary.critical ?? 0}
          tone="critical"
          active={tab === 'deals' && tier === 'critical'}
          onClick={() => showDeals('critical')}
        />
        <SummaryCard
          label="High"
          value={data?.summary.high ?? 0}
          active={tab === 'deals' && tier === 'high'}
          onClick={() => showDeals('high')}
        />
        <SummaryCard
          label="Medium"
          value={data?.summary.medium ?? 0}
          active={tab === 'deals' && tier === 'medium'}
          onClick={() => showDeals('medium')}
        />
        <SummaryCard
          label="Overdue tasks"
          value={taskCount('overdue')}
          tone="overdue"
          active={taskGroup === 'overdue'}
          onClick={() => showTasks('overdue')}
        />
        <SummaryCard
          label="Due today"
          value={taskCount('today')}
          active={taskGroup === 'today'}
          onClick={() => showTasks('today')}
        />
        <SummaryCard
          label="Next 7 days"
          value={taskCount('week')}
          active={taskGroup === 'week'}
          onClick={() => showTasks('week')}
        />
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200 mb-4" role="tablist">
        {(['deals', 'tasks'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-2 text-sm -mb-px border-b-[3px] rounded-t-lg transition-colors',
              tab === t
                ? 'border-brand-primary bg-white text-brand-darkest font-semibold'
                : 'border-transparent text-gray-500 hover:text-brand-dark hover:bg-white/60',
            )}
          >
            {t === 'deals' ? `Priority deals · ${data?.total ?? 0}` : `Tasks · ${myTasks.length}`}
          </button>
        ))}
      </div>

      {tab === 'tasks' ? (
        <FocusTasks ownerId={ownerId} group={taskGroup} onShowAll={() => showTasks(null)} />
      ) : (
        <div className={cn('transition-opacity', isPlaceholderData && 'opacity-60')}>
          {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {data && data.total === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500 text-sm">
              Nothing needs you right now — every open deal has a follow-up in hand.
            </div>
          )}
          {data && data.total > 0 && (
            <>
              <div
                className="flex flex-wrap items-center gap-1.5 mb-3"
                role="group"
                aria-label="Priority"
              >
                {([null, 'critical', 'high', 'medium'] as (FocusPriority | null)[]).map((t) => (
                  <button
                    key={t ?? 'all'}
                    type="button"
                    aria-pressed={tier === t}
                    onClick={() => setTier(t)}
                    className={cn(
                      'px-2.5 py-1 text-xs rounded-full border transition-colors',
                      tier === t
                        ? 'bg-brand-primary border-brand-primary text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-brand-primary/40',
                    )}
                  >
                    {t ? PRIORITY_LABEL[t] : 'All'}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-6 items-start">
                <div className="min-w-0">
                  {deals.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No {tier ? PRIORITY_LABEL[tier].toLowerCase() : ''} deals.
                    </p>
                  ) : (
                    <ul className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                      {deals.map((d) => (
                        <DealRow
                          key={d.dealId}
                          deal={d}
                          currency={currency}
                          selected={selected?.dealId === d.dealId}
                          onSelect={() => setSelectedId(d.dealId)}
                          onOpen={() => setOpenDeal(d.dealId)}
                        />
                      ))}
                    </ul>
                  )}
                  {data.total > data.deals.length && (
                    <p className="mt-2 text-xs text-gray-400">
                      Showing the {data.deals.length} highest of {data.total}.
                    </p>
                  )}
                </div>
                {selected && (
                  <InsightPanel
                    deal={selected}
                    currency={currency}
                    onOpen={() => setOpenDeal(selected.dealId)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}

      {openDeal && <DealDetail dealId={openDeal} onClose={() => setOpenDeal(null)} />}
    </PageContainer>
  )
}
